/**
 * @file    cellIndicators.tsx
 * @scope   SHARED
 * @summary Legacy (react-data-table) cell helpers: value extraction and trend indicators.
 * @consumes @cx/ui Icon, Tippy, react-icons
 * @produces getValueFromRow, getComparisonValueFromRow, getSignificanceValueFromRow
 * @sideEffects None.
 * @notes Differs from DynamicTable/common/utils/DynamicTableUtils on purpose: the legacy
 *        significance arrow carries a tooltip and the comparison guards on `undefined` cells.
 */
import React from 'react';
import { Icon } from '@cx/ui';
import Tippy from '@tippyjs/react';
import { HiArrowSmRight, HiArrowSmUp, HiOutlineArrowSmDown } from 'react-icons/hi';

export const getValueFromRow = (row: any, field: string) => {
  const fieldV = row[field];
  if (fieldV && typeof fieldV === 'object' && 'value' in fieldV) {
    return fieldV.value;
  }
  return fieldV;
};

const DifferenceTooltip: React.FC<{ difference: string; children: React.ReactElement }> = ({
  difference,
  children,
}) => (
  <Tippy
    content={
      <div title="">
        <div className="tooltip-title">{difference}</div>
      </div>
    }
  >
    {children}
  </Tippy>
);

export const getComparisonValueFromRow = (
  row: any,
  field: string,
  prevField: string,
  decimalPlaces?: number | string | null
) => {
  if (!row || row[field] === undefined || row[prevField] === undefined) {
    return <></>;
  }

  const currentValue = Number(row[field]?.value ?? row[field]);
  const prevValue = Number(row[prevField]?.value ?? row[prevField]);
  if (isNaN(currentValue) || isNaN(prevValue)) {
    return <></>;
  }

  const decimals = Number(decimalPlaces);
  const hasValidDecimalPlaces = Number.isInteger(decimals) && decimals >= 0;
  const difference = (currentValue - prevValue).toFixed(hasValidDecimalPlaces ? decimals : 2);

  if (currentValue > prevValue) {
    return (
      <DifferenceTooltip difference={difference}>
        <div className="pck-child-div significanceIndicator">
          <HiArrowSmUp className="biggest-green-arrow" />
        </div>
      </DifferenceTooltip>
    );
  }
  if (currentValue < prevValue) {
    return (
      <DifferenceTooltip difference={difference}>
        <div className="pck-child-div significanceIndicator">
          <HiOutlineArrowSmDown className="biggest-red-arrow" />
        </div>
      </DifferenceTooltip>
    );
  }
  return (
    <div className="pck-child-div significanceIndicator">
      <HiArrowSmRight className="biggest-yellow-arrow" />
    </div>
  );
};

const resolveSignificanceIcon = (cell: any) => {
  if (!cell.significanceIndicator) return null;
  if (cell.valueDifference > 0) return { name: 'metricArrowUp', className: 'biggest-green-arrow' };
  if (cell.valueDifference < 0) return { name: 'metricArrowDown', className: 'biggest-red-arrow' };
  if (cell.valueDifference == 0) {
    return { name: 'metricArrowRight', className: 'biggest-yellow-arrow' };
  }
  return null;
};

export const getSignificanceValueFromRow = (row: any, field: string) => {
  const cell = row[field];
  if (!cell || typeof cell !== 'object' || !('significanceIndicator' in cell)) return <></>;

  const icon = resolveSignificanceIcon(cell);
  if (!icon) return <></>;

  return (
    <Tippy
      content={Number(cell.valueDifference).toFixed(2)}
      placement="top"
      className="tooltip-container"
    >
      <div className="pck-child-div significanceIndicator">
        <Icon name={icon.name} className={icon.className}></Icon>
      </div>
    </Tippy>
  );
};
